// eslint-disable-next-line no-unused-vars

import { LS_ITEMS, addItemToLS, getItemFromLS } from 'utils/localStorage'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { currentWellDetailsSelector, currentWellSelector, userDefaultRoleSelector, userRoleSelector } from 'atoms'
import { useRecoilState, useSetRecoilState } from 'recoil'

import { useAuth0 } from '@auth0/auth0-react'
import useAxios from './useAxios'

const authContext = createContext()
export function ProvideAuth({ children }) {
  const auth = useAuth()
  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

const useInnovaAuth = () => {
  return useContext(authContext)
}

export default useInnovaAuth

const defAuthData = {
  role: null,
}

const useAuth = () => {
  const _isMounted = useRef()
  const [authType, setAuthType] = useState(getItemFromLS('authType', 'authType'))
  const [userRole, setUserRole] = useRecoilState(userRoleSelector)
  const [defaultUserRole, setDefaultUserRole] = useRecoilState(userDefaultRoleSelector)
  const setCurrentWell = useSetRecoilState(currentWellSelector)
  const setCurrentWellDetails = useSetRecoilState(currentWellDetailsSelector)
  const [, setConfig] = useState(null)
  const [user, setUser] = useState(null)
  const { isAuthenticated, loginWithRedirect, logout } = useAuth0()
  const databaseOrgRef = useRef('')
  const defaultDatabaseOrgRef = useRef('')
  const [databaseOrg, setDatabaseOrg] = useState('')
  const [defaultDatabaseOrg, setDefaultDatabaseOrg] = useState('')
  const [userDatabaseAccess, setUserDatabaseAccess] = useState([])

  const getRoleAndUser = useAxios({
    userName: user?.name,
    url: '/user/getUserData',
    timeout: 60000,
    databaseOrg: databaseOrgRef.current,
  })

  useEffect(() => {
    _isMounted.current = true

    return () => {
      _isMounted.current = false
    }
  }, [])

  useEffect(() => {
    let org =
      typeof getItemFromLS('databaseOrg', 'databaseOrg') === 'string' ? getItemFromLS('databaseOrg', 'databaseOrg') : ''
    let defaultOrg =
      typeof getItemFromLS('databaseDefaultOrg', 'databaseDefaultOrg') === 'string'
        ? getItemFromLS('databaseDefaultOrg', 'databaseDefaultOrg')
        : ''

    if (org === '' || defaultOrg === '') {
      return
    }

    databaseOrgRef.current = org
    defaultDatabaseOrgRef.current = defaultOrg

    setDatabaseOrg(org)
    setDefaultDatabaseOrg(defaultOrg)
  }, [])

  const getDatabaseOrg = () => {
    return databaseOrgRef.current
  }

  const getDefaultDatabaseOrg = () => {
    return defaultDatabaseOrgRef.current
  }

  const saveDatabaseOrg = (org) => {
    const orgName = typeof org === 'string' ? org : ''
    databaseOrgRef.current = orgName
    setDatabaseOrg(orgName)
    addItemToLS('databaseOrg', 'databaseOrg', orgName)
  }

  const saveDefaultDatabaseOrg = (org) => {
    const orgName = typeof org === 'string' ? org : ''
    defaultDatabaseOrgRef.current = orgName
    setDefaultDatabaseOrg(orgName)
    addItemToLS('databaseDefaultOrg', 'databaseDefaultOrg', orgName)
  }

  useEffect(() => {
    if (!_isMounted.current) return
    if (!isAuthenticated) return
    if (!user) return
    if (!user?.name || user?.name === '') return

    getRoleAndUserFunc(user.name)

    let databaseOrg = getDefaultDatabaseOrg()
    if (!databaseOrg || databaseOrg === '') {
      databaseOrg = getDatabaseOrg()
      getDefaultRoleAndUserFunc(user.name)
    }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  const getRoleAndUserFunc = async (username) => {
    let role = {}
    try {
      const response = await getRoleAndUser({ userName: username })

      if (response.error) {
        console.error('Error fetching role and user:', response.error)
      } else {
        role = response.data?.role || {}
        const dbAccess = response.data?.databaseAccessRecord || []
        setUserDatabaseAccess(dbAccess)

        setUserRole(role)
        setAuthType(role?.authType || '')
        addItemToLS('auth', 'currentAuth', {
          role: role,
        })
        if (role?.userPrefs?.unitPrefs != null) {
          addItemToLS('units', 'selectedUnits', role?.userPrefs?.unitPrefs)
        }
        addItemToLS('authType', 'authType', role?.authType || '')
        saveDatabaseOrg(role?.organization || '')
      }
    } catch (error) {
      console.error('Error fetching role and user:', error)
    }
    setConfig({
      role: role,
    })
  }

  const getDefaultRoleAndUserFunc = async (username) => {
    let role = {}
    try {
      const response = await getRoleAndUser({ userName: username })
      if (response.error) {
        console.error('Error fetching role and user:', response.error)
      } else {
        role = response.data?.role || {}
        setDefaultUserRole(role)
        saveDefaultDatabaseOrg(role?.organization || '')
      }
    } catch (error) {
      console.error('Error fetching role and user:', error)
    }
    setConfig({
      role: role,
    })
  }

  function cleanCurrentWellOnLogout() {
    let defaultWell = { organization: '', wellName: '' }
    let defaultWellDetails = {
      actualWell: '',
      wellStatus: '',
      operator: '',
      rig: '',
      jobNum: '',
      lat: '',
      lng: '',
    }
    addItemToLS(LS_ITEMS.currentWell, LS_ITEMS.currentWell, defaultWell)
    addItemToLS(LS_ITEMS.currentWellDetails, LS_ITEMS.currentWellDetails, defaultWellDetails)

    setCurrentWell(defaultWell)
    setCurrentWellDetails(defaultWellDetails)
  }

  function logoutAndCleanup() {
    addItemToLS('auth', 'currentAuth', defAuthData)
    if (defAuthData?.role?.userPrefs?.unitPrefs != null) {
      addItemToLS('units', 'selectedUnits', defAuthData?.role?.userPrefs?.unitPrefs)
    }
    addItemToLS('authType', 'authType', '')
    cleanCurrentWellOnLogout()
    saveDatabaseOrg('')
    saveDefaultDatabaseOrg('')
    setUser({ name: '' })
    addItemToLS('userName', 'userName', '')
    setUserRole('')
    setDefaultUserRole('')
    setAuthType('')
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  const login = async () => {
    loginWithRedirect()
  }

  async function loginApplicationUser(token, userName) {
    if (!token) return 'failed'
    if (!userName) return 'failed'
    if (typeof userName !== 'string') return 'failed userName format'

    addItemToLS('auth', 'currentAuth', defAuthData)
    if (defAuthData?.role?.userPrefs?.unitPrefs != null) {
      addItemToLS('units', 'selectedUnits', defAuthData?.role?.userPrefs?.unitPrefs)
    }
  }

  const changeUserRole = (org) => {
     setUserRole({
      ...userRole,
      organization: org,
    })
  }

  const getPermission = (org, perm) => {
    if (
      perm !== 'admin' &&
      perm !== 'canEdit' &&
      perm !== 'canDelete' &&
      perm !== 'canApprove' &&
      perm !== 'canCreate'
    ) {
      return false
    }

    if (userRole.innovaAdmin) return true

    if (!Array.isArray(userDatabaseAccess) || userDatabaseAccess.length === 0) return false
    const accessRecord = userDatabaseAccess.find((record) => record.organization === org)
    if (!accessRecord) return false

    if (perm !== 'admin' && accessRecord.role?.roleAttributes?.permissions?.admin) return true

    return accessRecord?.role?.roleAttributes?.permissions[perm]
  }

  return {
    login,
    logout: logoutAndCleanup,
    user,
    userRole,
    defaultUserRole,
    isAuthenticated,
    authType,
    setConfig,
    setUserRole,
    setUser,
    loginApplicationUser,
    setDefaultUserRole,
    getRoleAndUserFunc,
    databaseOrg,
    getDatabaseOrg,
    saveDatabaseOrg,
    defaultDatabaseOrg,
    getDefaultDatabaseOrg,
    saveDefaultDatabaseOrg,
    changeUserRole,
    getPermission,
  }
}
